import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { searchPageStyles, getResponsiveStyles } from '../components/search/SearchPage.styles';
import { useSearchPage, ENABLE_AI_SEARCH_OVERVIEW } from '../components/search/useSearchPage';
import TopSearchForm from '../components/search/TopSearchForm';
import HeaderButtons from '../components/search/HeaderButtons';
import SearchTypeTabs from '../components/search/SearchTypeTabs';
import BlockedMessage from '../components/search/BlockedMessage';
import SearchAccessBlockedPanel from '../components/search/SearchAccessBlockedPanel';
import AIOverviewChat from '../components/search/AIOverviewChat';
import WebSearchCard from '../components/search/WebSearchCard';
import MobileExploreNavBar from '../components/search/MobileExploreNavBar';
import { useSearchFeatureEnabled } from '../utils/searchFeaturePreferencesStore';
import { EXPLORE_PRIVACY_POLICY_URL } from '../config/legalUrls';

function SearchFeatureDisabledPanel() {
  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'var(--color-bg)',
        textAlign: 'center',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          maxWidth: 420,
          padding: 28,
          borderRadius: 16,
          border: '1px solid var(--color-border)',
          background: 'var(--color-bg-card)',
          boxShadow: '0 4px 24px var(--color-shadow)',
        }}
      >
        <div style={{ fontSize: 44, lineHeight: 1, marginBottom: 12 }} aria-hidden>
          🔒
        </div>
        <h1
          style={{
            fontSize: 'clamp(1.2rem, 4vw, 1.45rem)',
            fontWeight: 800,
            color: 'var(--color-text)',
            margin: '0 0 12px',
            lineHeight: 1.25,
          }}
        >
          Search is turned off on this device
        </h1>
        <p
          style={{
            margin: '0 0 18px',
            fontSize: 15,
            lineHeight: 1.55,
            color: 'var(--color-text)',
            opacity: 0.85,
          }}
        >
          A parent or teacher who can sign in can turn search back on in{' '}
          <strong>Settings</strong> → <strong>Learner</strong> → <strong>Search on this device</strong>.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'stretch' }}>
          <Link
            to="/"
            style={{
              display: 'block',
              padding: '12px 18px',
              borderRadius: 10,
              background: 'var(--color-primary)',
              color: '#fff',
              fontWeight: 700,
              textDecoration: 'none',
              fontSize: 15,
            }}
          >
            Back to Home
          </Link>
          <Link
            to="/settings"
            style={{
              display: 'block',
              padding: '12px 18px',
              borderRadius: 10,
              border: '1px solid var(--color-border)',
              color: 'var(--color-text)',
              fontWeight: 700,
              textDecoration: 'none',
              fontSize: 15,
              background: 'var(--color-bg)',
            }}
          >
            Open Settings
          </Link>
        </div>
        <p style={{ margin: '18px 0 0', fontSize: 13, lineHeight: 1.45, opacity: 0.72 }}>
          <a
            href={EXPLORE_PRIVACY_POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--color-primary)', fontWeight: 700 }}
          >
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
}

function SearchPageContent({ searchType }) {
  const { user, loading } = useAuth();

  // Add state for scroll detection
  const [showDownArrow, setShowDownArrow] = useState(false);
  const [isScrolledUp, setIsScrolledUp] = useState(false);
  const [exploreBarReserve, setExploreBarReserve] = useState(0);
  const chatInputBuffer = 32;
  const bottomInset = exploreBarReserve + chatInputBuffer;
  const bottomInsetRef = useRef(bottomInset);
  bottomInsetRef.current = bottomInset;

  const handleExploreBarReserve = useCallback((px) => {
    setExploreBarReserve(typeof px === 'number' ? px : 0);
  }, []);

  const topSearchBarRef = useRef(null);
  const [mainContentPadTop, setMainContentPadTop] = useState(112);

  const {
    // State
    aiOverview,
    currentSearchType,
    aiLoading,
    aiResponseType,
    showAiOverview,
    gcsLoaded,
    chatMessages,
    isChatFocused,
    searchInput,
    headerScrolled,
    blockedMessage,
    blockedReason,
    aiFullScreen,
    profileOpen,
    aiOverviewVisible,
    
    // Refs
    searchResultsRef,
    searchBoxRef,
    chatMessagesRef,
    lastMessageRef,
    mainContainerRef,
    profileRef,
    
    // Functions
    setCurrentSearchType,
    setShowAiOverview,
    setGcsLoaded,
    setChatMessages,
    setSearchInput,
    setHeaderScrolled,
    setBlockedMessage,
    setAiFullScreen,
    setProfileOpen,
    
    handleNewSearch,
    handleMiniAppFocus,
    handleTopSearch,
    scrollToLatestMessage,
    scrollAIOverviewIntoView,
    
    // Query
    query,
    navigate,
  } = useSearchPage(searchType);

  useLayoutEffect(() => {
    const el = topSearchBarRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      setMainContentPadTop(headerScrolled ? 96 : 120);
      return;
    }
    const measure = () => {
      const h = el.getBoundingClientRect().height;
      setMainContentPadTop(Math.max(72, Math.ceil(h) + 6));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [headerScrolled]);

  useEffect(() => {
    if (!profileOpen) return;
    function handleClickOutside(event) {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen, profileRef, setProfileOpen]);

  // Handle window resize for mobile responsiveness (AI overview only)
  useEffect(() => {
    if (!ENABLE_AI_SEARCH_OVERVIEW) return;
    const handleResize = () => {
      if (window.innerWidth <= 768 && (aiOverview || aiLoading)) {
        setTimeout(() => {
          scrollAIOverviewIntoView();
        }, 100);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [aiOverview, aiLoading, scrollAIOverviewIntoView]);

  // Scroll detection for down arrow visibility (AI / chat column)
  useEffect(() => {
    if (!ENABLE_AI_SEARCH_OVERVIEW) {
      setShowDownArrow(false);
      return;
    }
    const handleScroll = () => {
      const mainContainer = mainContainerRef.current;
      if (!mainContainer) return;

      const { scrollTop, scrollHeight, clientHeight } = mainContainer;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px tolerance
      
      const hasAIOverviewContent = aiOverview || aiLoading || chatMessages.length > 0;
      const shouldShow = hasAIOverviewContent && !isAtBottom;
      
      setShowDownArrow(shouldShow);
      setIsScrolledUp(!isAtBottom);
    };

    const mainContainer = mainContainerRef.current;
    if (mainContainer) {
      mainContainer.addEventListener('scroll', handleScroll);
      return () => mainContainer.removeEventListener('scroll', handleScroll);
    }
  }, [aiOverview, aiLoading, chatMessages.length]);

  // Auto-scroll to latest message when new messages are added (mobile only; AI / chat UI)
  useEffect(() => {
    if (!ENABLE_AI_SEARCH_OVERVIEW) return;
    if (window.innerWidth <= 768 && chatMessages.length > 0) {
      // Check if the last message is from assistant (not user)
      const lastMessage = chatMessages[chatMessages.length - 1];
      if (lastMessage && lastMessage.type === 'assistant' && !lastMessage.isTyping) {
        // Small delay to ensure the message is rendered
        setTimeout(() => {
          scrollToLatestResponse();
        }, 100);
      }
    }
  }, [chatMessages]);

  // Improved scroll to latest message - scroll to top of new response
  const scrollToLatestResponse = () => {
    setTimeout(() => {
      const mainContainer = mainContainerRef.current;
      const lastMessage = lastMessageRef.current;
      const inset = bottomInsetRef.current;

      if (mainContainer && lastMessage) {
        const lastMessageRect = lastMessage.getBoundingClientRect();
        const containerRect = mainContainer.getBoundingClientRect();

        const messageTop = lastMessageRect.top - containerRect.top;
        const messageBottom = lastMessageRect.bottom - containerRect.top;
        const containerHeight = mainContainer.clientHeight - inset;

        if (window.innerWidth <= 768) {
          const messageTopAbsolute = lastMessageRect.top - containerRect.top + mainContainer.scrollTop;
          const buffer = chatInputBuffer;
          const targetScrollTop = messageTopAbsolute - inset - buffer;

          mainContainer.scrollTo({
            top: Math.max(0, targetScrollTop),
            behavior: 'smooth',
          });
          return;
        }

        if (messageTop >= 0 && messageBottom <= containerHeight) {
          return;
        }

        const messageTopAbsolute = lastMessageRect.top - containerRect.top + mainContainer.scrollTop;
        const buffer = chatInputBuffer;
        const targetScrollTop = messageTopAbsolute - inset - buffer;

        mainContainer.scrollTo({
          top: Math.max(0, targetScrollTop),
          behavior: 'smooth',
        });
      } else {
        scrollToLatestMessage();
      }
    }, 100);
  };

  const hasAIOverviewContent =
    ENABLE_AI_SEARCH_OVERVIEW && (aiOverview || aiLoading || chatMessages.length > 0);
  const shouldShowDownArrowFinal = showDownArrow && hasAIOverviewContent;

  return (
    <div
      ref={mainContainerRef}
      className="search-main-container"
      style={{
        ...searchPageStyles.mainContainer,
        paddingBottom: bottomInset,
      }}
    >
      <div style={{
        ...searchPageStyles.contentWrapper,
        paddingTop: mainContentPadTop,
      }}>
        {/* Top Search Box — height drives paddingTop so tabs/results clear the fixed bar + notice */}
        <TopSearchForm
          ref={topSearchBarRef}
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          handleTopSearch={handleTopSearch}
          headerScrolled={headerScrolled}
        />

        {/* Header with Login/Profile Button */}
        <HeaderButtons
          loading={loading}
          user={user}
          headerScrolled={headerScrolled}
          profileOpen={profileOpen}
          setProfileOpen={setProfileOpen}
          profileRef={profileRef}
          navigate={navigate}
        />

        {/* Search Type Tabs */}
        <SearchTypeTabs
          currentSearchType={currentSearchType}
          setCurrentSearchType={setCurrentSearchType}
          query={query}
        />
        
        <SearchAccessBlockedPanel reason={blockedReason} />
        <BlockedMessage blockedMessage={blockedMessage} />
        
        {/* Main Content Area */}
        <div
          className="search-main-content"
          style={{
            ...searchPageStyles.mainContent,
            flexDirection:
              ENABLE_AI_SEARCH_OVERVIEW && aiResponseType === 'direct_response' ? 'row' : 'column',
          }}
        >
          {/* AI overview / “thinking” panel — gated by ENABLE_AI_SEARCH_OVERVIEW in useSearchPage.js */}
          {ENABLE_AI_SEARCH_OVERVIEW
            ? (() => {
                const webCondition =
                  currentSearchType === 'web' &&
                  (aiLoading || aiResponseType === 'direct_response' || chatMessages.length > 0);
                const otherCondition =
                  currentSearchType !== 'web' && (chatMessages.length > 0 || isChatFocused);
                const shouldShow = webCondition || otherCondition;

                if (aiFullScreen && window.innerWidth > 768) {
                  return (
                    <>
                      <AIOverviewChat
                        aiLoading={aiLoading}
                        aiOverview={aiOverview}
                        chatMessages={chatMessages}
                        aiFullScreen={aiFullScreen}
                        setAiFullScreen={setAiFullScreen}
                        scrollToLatestMessage={scrollToLatestResponse}
                        handleMiniAppFocus={handleMiniAppFocus}
                        lastMessageRef={lastMessageRef}
                        chatMessagesRef={chatMessagesRef}
                      />
                      <div style={{ height: bottomInset }} />
                    </>
                  );
                }
                return shouldShow ? (
                  <>
                    <AIOverviewChat
                      aiLoading={aiLoading}
                      aiOverview={aiOverview}
                      chatMessages={chatMessages}
                      aiFullScreen={aiFullScreen}
                      setAiFullScreen={setAiFullScreen}
                      scrollToLatestMessage={scrollToLatestResponse}
                      handleMiniAppFocus={handleMiniAppFocus}
                      lastMessageRef={lastMessageRef}
                      chatMessagesRef={chatMessagesRef}
                    />
                    <div style={{ height: bottomInset }} />
                  </>
                ) : null;
              })()
            : null}
          
          {/* Web Search Card */}
          {!blockedMessage && !blockedReason && (
            <WebSearchCard
              currentSearchType={currentSearchType}
              aiFullScreen={ENABLE_AI_SEARCH_OVERVIEW && aiFullScreen}
              aiOverviewVisible={aiOverviewVisible}
              aiResponseType={aiResponseType}
              searchResultsRef={searchResultsRef}
              gcsLoaded={gcsLoaded}
              handleNewSearch={handleNewSearch}
            />
          )}
        </div>
      </div>

      {/* Transparent Down Arrow - above bottom explore bar */}
      {shouldShowDownArrowFinal && (
        <div
          style={{
            position: 'fixed',
            bottom: Math.max(88, bottomInset + 28),
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 150,
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            background: 'rgba(0, 0, 0, 0.1)',
            borderRadius: '50%',
            backdropFilter: 'blur(4px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            transition: 'all 0.3s ease',
            animation: 'fadeInUp 0.3s ease-out',
          }}
          onClick={scrollToLatestResponse}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(0, 0, 0, 0.2)';
            e.target.style.transform = 'translateX(-50%) scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(0, 0, 0, 0.1)';
            e.target.style.transform = 'translateX(-50%) scale(1)';
          }}
          title="Scroll to latest response"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="rgba(255, 255, 255, 0.8)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M7 13l5 5 5-5" />
            <path d="M7 6l5 5 5-5" />
          </svg>
        </div>
      )}

      <MobileExploreNavBar
        chatInputBottomPx={0}
        onReserveHeightChange={handleExploreBarReserve}
      />

      <style>
        {getResponsiveStyles()}
        {`
          /* Hide Google CSE 'Search for...' and 'New Search' options */
          .gsc-resultsHeader, .gsc-above-wrapper-area {
            display: none !important;
          }

          /* Hide CSE refinement tabs — app SearchTypeTabs handles category switching */
          .gsc-refinementsArea,
          .gsc-tabsArea {
            display: none !important;
          }
          
          /* Animation for down arrow */
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateX(-50%) translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
          }
        `}
      </style>
    </div>
  );
};

function SearchPage({ searchType }) {
  const [searchFeatureEnabled] = useSearchFeatureEnabled();
  if (!searchFeatureEnabled) {
    return <SearchFeatureDisabledPanel />;
  }
  return <SearchPageContent searchType={searchType} />;
}

export default SearchPage;